import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Device as DeviceCapacitor } from '@capacitor/device';
import { BusinessDataType } from '@am3nd-org/pointcraft-types';
import React, { useMemo, useState, useEffect, MouseEvent, useCallback } from 'react';

import {
  List,
  Stack,
  Dialog,
  Button,
  Select,
  Divider,
  Tooltip,
  ListItem,
  MenuItem,
  TextField,
  Typography,
  DialogProps,
  DialogTitle,
  FormControl,
  DialogContent,
} from '@mui/material';

import { UpdateOrCreateDevice } from 'src/utils/post/device';
import { closeAllServices } from 'src/utils/Providers/allSystem';
import { setCurrentDeviceInDB, getCurrentDeviceFromDB } from 'src/utils/Services/DB_Utils';

import { usePopover } from 'src/version-3/hooks';
import { useSystemStore } from 'src/store/SystemStore';
import SvgIconBox from 'src/version-3/components/svg-icon';
import restartIcon from 'src/version-3/assets/icons/restart-icon.svg';
import { useAthServerStatus } from 'src/version-3/utils/system-status';
import { DeviceType, IDeviceItem, ITerminalItem } from 'src/store/SystemStore/types';
import SystemStatus from 'src/version-3/components/popovers/system-status/SystemStatus';

import FormProvider from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';

import BluetoothReaderConnectionDialog from './bluetooth-reader-connection-dialog';

// Types
interface SelectBusinessDeviceDialogProps extends DialogProps {
  business: BusinessDataType;
  open: boolean;
  onClose: VoidFunction;
}

interface DeviceFormData {
  name: string;
  terminalId: string;
}

interface StoredDeviceInfo {
  id: string | null;
  name: string;
  type: DeviceType;
  initialized: boolean;
}

interface TerminalOption {
  value: string;
  label: string;
}

// Constants
const DEFAULT_FORM_VALUES: DeviceFormData = {
  name: '',
  terminalId: '',
};

const DEVICE_CREATION_DELAY = 500;

// Validation schema
const deviceValidationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Device name is required'),
  terminalId: Yup.string()
    .required('Terminal is required')
    .notOneOf([''], 'Terminal selection is required'),
});

// Utility functions
const createTerminalOption = (terminal: ITerminalItem): TerminalOption => ({
  value: terminal.id,
  label: `${terminal.name} (${terminal.deviceTag})`,
});

const findDeviceById = (devices: IDeviceItem[], deviceId: string): IDeviceItem | undefined =>
  devices.find((device) => device.id === deviceId);

const findTerminalById = (
  terminals: ITerminalItem[],
  terminalId: string
): ITerminalItem | undefined => terminals.find((terminal) => terminal.id === terminalId);

const isCurrentDevice = (storedDevice: StoredDeviceInfo | null, deviceId: string): boolean =>
  storedDevice?.id !== null && storedDevice?.id === deviceId;

const getUsedTerminalIds = (devices: IDeviceItem[]): Set<string> =>
  new Set(devices.flatMap((device) => (device.terminal?.id ? [device.terminal.id] : [])));

const getAvailableTerminals = (
  terminals: ITerminalItem[],
  usedTerminalIds: Set<string>
): ITerminalItem[] => terminals.filter((terminal) => !usedTerminalIds.has(terminal.id));

export default function SelectBusinessDeviceDialog({
  business,
  open,
  onClose,
  ...dialogProps
}: SelectBusinessDeviceDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { setCurrentDevice, currentDeviceType, devices, terminals, setDevices, appVersion } = useSystemStore();

  // System status + restart
  const systemStatusPopover = usePopover();
  const { isOnline, mutate } = useAthServerStatus({
    enabled: true,
    dedupingInterval: 8000,
    refreshInterval: 0,
  });

  // State management
  const [storedCurrentDevice, setStoredCurrentDevice] = useState<StoredDeviceInfo | null>(null);
  const [deviceTerminalMap, setDeviceTerminalMap] = useState<Record<string, string>>({});
  const [openBleConnectionDialog, setOpenBleConnectionDialog] = useState(false);
  const [createdDeviceName, setCreatedDeviceName] = useState<string>('');

  // Form setup
  const formMethods = useForm<DeviceFormData>({
    resolver: yupResolver(deviceValidationSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = formMethods;

  // Initialize stored device and terminal mapping
  useEffect(() => {
    const initializeDeviceData = async () => {
      try {
        const currentDeviceResult = await getCurrentDeviceFromDB();
        const currentDevice = currentDeviceResult?.data ?? null;
        setStoredCurrentDevice(currentDevice);

        if (currentDevice && currentDevice.id && devices.length > 0) {
          const matchingDevice = devices.find(
            (device) => device.id === currentDevice.id || device.id === (currentDevice as any).linkedId || device.id === currentDevice.capacitorDeviceId
          );
          if (matchingDevice) {
            setCurrentDevice({
              id: currentDevice.id,
              name: currentDevice.name,
              type: currentDevice.type,
              initialized: currentDevice.initialized,
          });
          }
        }

        // Initialize terminal mapping
        const initialTerminalMap: Record<string, string> = {};
        devices.forEach((device) => {
          if (device.terminal?.id) {
            initialTerminalMap[device.id] = device.terminal.id;
          }
        });
        setDeviceTerminalMap(initialTerminalMap);
      } catch (error) {
        console.error('Failed to initialize device data:', error);
      }
    };

    initializeDeviceData();
  }, [devices, setCurrentDevice]);

  // Memoized terminal options
  const allTerminalOptions = useMemo(() => terminals?.map(createTerminalOption), [terminals]);

  // const availableTerminalOptions = useMemo(() => {
  //   const usedTerminalIds = getUsedTerminalIds(devices);
  //   const availableTerminals = getAvailableTerminals(terminals, usedTerminalIds);
  //   return availableTerminals?.map(createTerminalOption);
  // }, [devices, terminals]);

  // Event handlers
  const handleDeviceSelection = async (deviceId: string): Promise<void> => {
    try {
      console.log('Selecting device:', deviceId);

      const selectedDevice = findDeviceById(devices, deviceId);
      if (!selectedDevice) {
        console.error('Selected device not found:', deviceId);
        return;
      }

      const currentCapacitorDevice = await DeviceCapacitor.getId();

      await setCurrentDeviceInDB({
        id:  selectedDevice.id,
        name: selectedDevice.name,
        type: currentDeviceType,
        initialized: true,
        capacitorDeviceId: currentCapacitorDevice.identifier,
      });

      setCurrentDevice({
        id: selectedDevice.id,
        name: selectedDevice.name,
        type: DeviceType.DESKTOP,
        initialized: true,
      });

      onClose();
    } catch (error) {
      console.error('Device selection failed:', error);
      enqueueSnackbar('Device selection failed', { variant: 'error' });
    }
  };

  const handleTerminalAssignment = (deviceId: string, terminalId: string): void => {
    setDeviceTerminalMap((previousMap) => ({
      ...previousMap,
      [deviceId]: terminalId,
    }));

    // Update devices with new terminal assignment
    const updatedDevices = devices.map((device) => {
      if (device.id === deviceId) {
        const assignedTerminal = findTerminalById(terminals, terminalId);
        return { ...device, terminal: assignedTerminal };
      }
      return device;
    });

    setDevices(updatedDevices);
    // TODO: Implement API call to update device terminal assignment
  };

  const handleDeviceCreation = handleSubmit(async (formData: DeviceFormData): Promise<void> => {
    try {
      const currentCapacitorDevice = await DeviceCapacitor.getId();

      // Simulate processing delay
      await new Promise((resolve) => setTimeout(resolve, DEVICE_CREATION_DELAY));

      // Check if Bluetooth Reader was selected
      const isBluetoothReader = formData.terminalId === 'BLUETOOTH_READER';
      const assignedTerminal = isBluetoothReader
        ? null
        : findTerminalById(terminals, formData.terminalId);

      const newDevice: IDeviceItem = {
        id: uuidv4(),
        name: formData.name,
        terminal: assignedTerminal || undefined, // Convert null to undefined for type compatibility
        serialNumber: currentCapacitorDevice.identifier,
        isActive: true,
        lastConnected: new Date().toISOString(),
        // deviceTag: 'tmr_GCNofwNKjbMLP3',
        // locationId: 'tml_FuR9ywczjnwKqt',
        primary: devices.length === 0,
      };

      const updatedDevices = [...devices, newDevice];
      const updateResult = await UpdateOrCreateDevice(updatedDevices, business.id);

      if (!updateResult || updateResult.status !== 'SUCCESS') {
        throw new Error('Failed to update device on server');
      }

      await setCurrentDeviceInDB({
        id: newDevice.id,
        name: newDevice.name,
        type: currentDeviceType,
        capacitorDeviceId: currentCapacitorDevice.identifier,
        initialized: true,
      });

      setCurrentDevice({
        id: newDevice.id,
        name: newDevice.name,
        type: currentDeviceType,
        initialized: true,
      });

      enqueueSnackbar('Device created successfully!', { variant: 'success' });

      // Handle dialog closing based on device type
      if (isBluetoothReader) {
        // Set up for Bluetooth connection dialog
        setCreatedDeviceName(formData.name);
        setOpenBleConnectionDialog(true);
        // Don't close the main dialog - it will close after BLE dialog is handled
      } else {
        // For regular terminals, close the dialog immediately
        onClose();
      }

    } catch (error) {
      console.error('Device creation failed:', error);
      enqueueSnackbar('Device creation failed', { variant: 'error' });
    }
  });

  const handleSystemStatusClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      mutate();
      systemStatusPopover.handleToggle();
    },
    [systemStatusPopover, mutate]
  );

  const handleRestartSystems = useCallback(async () => {
    try {
      await closeAllServices();
      window.location.reload();
    } catch (error) {
      console.error('Error reloading app:', error);
    }
  }, []);

  // Render helpers
  const renderDeviceList = () => (
    <List
      disablePadding
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '& .MuiListItem-root': { py: 2 },
      }}
    >
      {devices?.length === 0 ? (
        <ListItem>
          <Typography variant="body2" color="text.secondary">
            No devices registered yet.
          </Typography>
        </ListItem>
      ) : (
        devices?.map((device, index) => (
          <React.Fragment key={device.id}>
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <Stack direction="column" spacing={1} sx={{ width: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 600 }}>
                    {device.name}
                  </Typography>
                  {isCurrentDevice(storedCurrentDevice, device.id) && (
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 600 }} color="success.main">
                      (Current)
                    </Typography>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  SN: {device.serialNumber ?? '—'}
                </Typography>

                <FormControl size="small" sx={{ mt: 1, width: '80%' }}>
                  <Select
                    displayEmpty
                    value={deviceTerminalMap[device.id] ?? ''}
                    onChange={(event) =>
                      handleTerminalAssignment(device.id, event.target.value as string)
                    }
                    renderValue={(selectedValue) => {
                      if (selectedValue === 'BLUETOOTH_READER') {
                        return 'Bluetooth Reader (Local)';
                      }
                      return selectedValue
                        ? allTerminalOptions.find((option) => option.value === selectedValue)?.label
                        : 'Select terminal…';
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="BLUETOOTH_READER">
                      <em>Bluetooth Reader (Local)</em>
                    </MenuItem>
                    {allTerminalOptions?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleDeviceSelection(device.id)}
              >
                Select
              </Button>
            </ListItem>
            {index !== devices.length - 1 && <Divider />}
          </React.Fragment>
        ))
      )}
    </List>
  );

  const renderDeviceCreationForm = () => (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mt={2}>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="New device name"
            variant="outlined"
            size="small"
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />

      <Controller
        name="terminalId"
        control={control}
        render={({ field }) => (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              {...field}
              displayEmpty
              renderValue={(selectedValue) => {
                if (selectedValue === 'BLUETOOTH_READER') {
                  return 'Bluetooth Reader (Local)';
                }
                return selectedValue
                  ? allTerminalOptions.find((option) => option.value === selectedValue)?.label
                  : 'Select terminal';
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="BLUETOOTH_READER">
                <em>Bluetooth Reader (Local)</em>
              </MenuItem>
              {allTerminalOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Button
        type="submit"
        variant="contained"
        color="info"
        sx={{ whiteSpace: 'nowrap' }}
        disabled={isSubmitting || !isValid}
      >
        {isSubmitting ? 'Creating…' : 'Create'}
      </Button>
    </Stack>
  );

  const renderStatusBar = () => (
    <>
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center" pt={1}>
        <Tooltip title="Device Status" placement="top">
          <Button
            ref={systemStatusPopover.anchorRef}
            onClick={handleSystemStatusClick}
            variant="text"
            size="small"
            sx={{
              px: 1.5,
              borderRadius: '6px',
              border: `1px solid ${isOnline ? '#28D59E' : '#FF465C'}`,
              bgcolor: isOnline ? 'rgba(40, 213, 158, 0.10)' : 'rgba(255, 70, 92, 0.10)',
              fontWeight: 700,
              color: isOnline ? '#28D59E' : '#FF465C',
              fontSize: '12px',
            }}
          >
            {isOnline ? 'Currently Online' : 'Offline'}
          </Button>
        </Tooltip>

        <Button
          onClick={handleRestartSystems}
          variant="text"
          size="small"
          sx={{ color: 'text.secondary', fontWeight: 700 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <SvgIconBox src={restartIcon} alt="restart" />
            <Typography sx={{ textDecoration: 'underline', fontSize: '11px', fontWeight: 700 }}>
              Restart Systems
            </Typography>
          </Stack>
        </Button>
      </Stack>
    </>
  );

  // Handle BLE connection dialog close
  const handleBleConnectionDialogClose = () => {
    setOpenBleConnectionDialog(false);
    setCreatedDeviceName('');
    onClose(); // Close the main dialog after BLE dialog is closed
  };

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose} {...dialogProps}>
        <DialogTitle sx={{ typography: 'h6', textAlign: 'center' }}>
          Select Business Device
        </DialogTitle>

        <DialogContent sx={{ overflow: 'unset', py: 4 }}>
          <FormProvider methods={formMethods} onSubmit={handleDeviceCreation}>
            <Stack spacing={3}>
              <Typography variant="subtitle1" fontWeight={600}>
                Current Devices
              </Typography>

              {renderDeviceList()}
              {renderDeviceCreationForm()}
              {renderStatusBar()}
            </Stack>
          </FormProvider>
        </DialogContent>
      </Dialog>

      {/* Bluetooth Reader Connection Dialog */}
      <BluetoothReaderConnectionDialog
        open={openBleConnectionDialog}
        onClose={handleBleConnectionDialogClose}
        deviceName={createdDeviceName}
      />

      <SystemStatus
        open={systemStatusPopover.open}
        anchorElement={systemStatusPopover.anchorElement}
        onClose={systemStatusPopover.handleClose}
        appVersion={appVersion.current}
      />
    </>
  );
}
